import type { AssessmentSignals, Conflict, OutsideInView } from '@cyberscore/contracts';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { can, useSession } from '../../lib/session';
import { useFollowUps } from '../follow-ups/api';
import { useScore } from '../portfolio/api';

/**
 * What a record's insights are ranked from (task 99): the score, conflicts, follow-ups and the internet
 * check of one assessment. Each comes from its own route, asked for only when the person holds that
 * route's permission, so an insight never shows what its reader couldn't open.
 */
export function useAssessmentSignals(id: string | null) {
  const { tenant } = useSession();
  const aid = id ?? '';
  const score = useScore(aid);
  const conflicts = useQuery({
    queryKey: ['assessments', tenant?.id, aid, 'conflicts'],
    queryFn: () => api<Conflict[]>('GET', `/assessments/${aid}/conflicts`),
    enabled: !!tenant && !!id && can(tenant, 'evidence.view'),
  });
  const followUps = useFollowUps(aid, !!id && can(tenant, 'assessments.view'));
  const outside = useQuery({
    queryKey: ['outside-in', tenant?.id, aid],
    queryFn: () => api<{ view: OutsideInView | null }>('GET', `/assessments/${aid}/outside-in`),
    enabled: !!tenant && !!id && can(tenant, 'outside_in.view'),
  });
  const signals: AssessmentSignals = {
    score: score.data ? { result: score.data.score, trace: score.data.explanation } : null,
    conflicts: conflicts.data ?? null,
    follow_ups: followUps.data ?? null,
    outside_in: outside.data?.view ?? null,
  };
  return {
    signals,
    score,
    conflicts: conflicts.data ?? [],
    followUps: followUps.data ?? [],
    loading: !!id && score.isPending,
  };
}
